/**
 * Solitaire — sound palette.
 *
 * Aesthetic: real cards on a wool-felt table. Every sound is a TEXTURE, never
 * a beep:
 *   - slide   — a card skating across felt: a short low-passed noise swell
 *               with a faint pitched body, a soft frictiony "shhk."
 *   - flip    — a card turned face-up: a crisp paper "tick" (band-passed noise
 *               transient) over a tiny wooden knock.
 *   - place   — a card set down onto a pile: a muffled felt "thup," lower and
 *               rounder than the flip.
 *   - deal    — like place but lighter & higher, used in the deal cascade.
 *   - draw    — stock → waste: a paper riffle, two quick ticks.
 *   - lift    — a card picked up: a tiny breathy noise, the lightest sound.
 *   - nudge   — a soft "not there" — a low rounded thud, never a buzzer.
 *   - auto    — the satisfying snap of a card flying home to its foundation:
 *               place + a small rising chime so it reads as PROGRESS.
 *   - win     — the cascade flourish: a warm felt sweep under a slow, bright
 *               ascending chord (a major add9, ~1.4s) — the payoff.
 *   - nav     — hub / page transitions: a single soft felt-brushed swell.
 *
 * Everything routes through `audio.playIfNotMuted`, so the global mute gate is
 * respected automatically. These syntheses are bespoke to Solitaire — no key,
 * waveform, or envelope is shared with Five or the Reading Room.
 */

import { audio } from '@/juice';

import type { HapticStyle } from '@rendever/games-contracts';

export type SolitaireSoundName =
  | 'slide'
  | 'flip'
  | 'place'
  | 'deal'
  | 'draw'
  | 'lift'
  | 'nudge'
  | 'auto'
  | 'win'
  | 'nav';

export const soundHaptic: Partial<Record<SolitaireSoundName, HapticStyle>> = {
  slide: 'light',
  flip: 'light',
  place: 'medium',
  deal: 'light',
  draw: 'light',
  lift: 'light',
  nudge: 'warning',
  auto: 'success',
  win: 'success',
  nav: 'light',
};

// ---------------------------------------------------------------------------
// Small random helpers — jitter keeps repeated cards from sounding identical.
// ---------------------------------------------------------------------------

/** ±cents pitch jitter as a multiplier. */
function centsJitter(cents = 18): number {
  const c = (Math.random() * 2 - 1) * cents;
  return Math.pow(2, c / 1200);
}

/** ±dB level jitter as a linear multiplier. */
function dbJitter(db = 2): number {
  const d = (Math.random() * 2 - 1) * db;
  return Math.pow(10, d / 20);
}

// ---------------------------------------------------------------------------
// Texture primitives
// ---------------------------------------------------------------------------

/**
 * Felt friction — a band of filtered noise that swells and fades. The heart of
 * the "card on felt" feel. `centerHz` sets how bright the friction reads.
 */
function feltNoise(opts: {
  durationMs: number;
  centerHz: number;
  q?: number;
  peak?: number;
  sweepTo?: number;
}): void {
  const ctx = audio.getAudioContext();
  const master = audio.getMasterGain();
  if (!ctx || !master) return;
  const t = ctx.currentTime;
  const dur = opts.durationMs / 1000;
  const peak = (opts.peak ?? 0.16) * dbJitter();

  const buf = audio.noiseBuffer(ctx, { lengthMs: opts.durationMs + 40, shaping: 1.6 });
  const src = ctx.createBufferSource();
  src.buffer = buf;

  const bp = ctx.createBiquadFilter();
  bp.type = 'bandpass';
  bp.frequency.setValueAtTime(opts.centerHz, t);
  if (opts.sweepTo) {
    bp.frequency.exponentialRampToValueAtTime(opts.sweepTo, t + dur);
  }
  bp.Q.value = opts.q ?? 0.8;

  // A gentle low-pass on top so the friction stays warm, not hissy.
  const lp = ctx.createBiquadFilter();
  lp.type = 'lowpass';
  lp.frequency.value = 2600;

  const { node: g } = audio.envelope(ctx, {
    attack: 0.012,
    decay: 0.04,
    sustain: 0.5,
    release: dur,
    peak,
  });
  audio.connectThrough(src, bp, lp, g, master);
  src.start(t);
  src.stop(t + dur + 0.05);
}

/**
 * A short wooden / paper KNOCK — a quick pitched body with a downward sweep
 * plus a noise transient. `pitch` low = a felt "thup", high = a paper "tick".
 */
function knock(opts: { pitch: number; durationMs?: number; peak?: number; noise?: number }): void {
  const ctx = audio.getAudioContext();
  const master = audio.getMasterGain();
  if (!ctx || !master) return;
  const t = ctx.currentTime;
  const dur = (opts.durationMs ?? 70) / 1000;
  const pitch = opts.pitch * centsJitter();
  const peak = (opts.peak ?? 0.2) * dbJitter();

  const osc = audio.makeOscillator(ctx, { freq: pitch, type: 'triangle' });
  osc.frequency.exponentialRampToValueAtTime(pitch * 0.5, t + dur);
  const { node: g } = audio.envelope(ctx, {
    attack: 0.001,
    decay: 0.012,
    sustain: 0.3,
    release: dur,
    peak,
  });
  audio.connectThrough(osc, g, master);
  osc.start(t);
  osc.stop(t + dur + 0.02);

  const noiseAmt = opts.noise ?? 0.55;
  if (noiseAmt > 0) {
    const buf = audio.noiseBuffer(ctx, { lengthMs: 20 });
    const src = ctx.createBufferSource();
    src.buffer = buf;
    const bp = ctx.createBiquadFilter();
    bp.type = 'bandpass';
    bp.frequency.value = pitch * 3.2;
    bp.Q.value = 1.4;
    const { node: ng } = audio.envelope(ctx, {
      attack: 0.001,
      release: 0.028,
      peak: peak * noiseAmt,
    });
    audio.connectThrough(src, bp, ng, master);
    src.start(t);
    src.stop(t + 0.04);
  }
}

/** A soft sung tone with a warm body — used for the chime in `auto` / `win`. */
function tone(opts: { freq: number; durationMs: number; peak?: number; delayMs?: number; bright?: boolean }): void {
  const ctx = audio.getAudioContext();
  const master = audio.getMasterGain();
  if (!ctx || !master) return;
  const t = ctx.currentTime + (opts.delayMs ?? 0) / 1000;
  const dur = opts.durationMs / 1000;
  const peak = opts.peak ?? 0.14;
  const freq = opts.freq;

  const osc = audio.makeOscillator(ctx, { freq, type: 'triangle' });
  const lp = ctx.createBiquadFilter();
  lp.type = 'lowpass';
  lp.frequency.value = opts.bright ? 3600 : 2000;
  lp.Q.value = 0.4;

  const g = ctx.createGain();
  g.gain.setValueAtTime(0.0001, t);
  g.gain.exponentialRampToValueAtTime(peak, t + 0.02);
  g.gain.exponentialRampToValueAtTime(0.0001, t + dur);
  audio.connectThrough(osc, lp, g, master);
  osc.start(t);
  osc.stop(t + dur + 0.05);

  // Perfect-fifth shimmer for warmth.
  const osc2 = audio.makeOscillator(ctx, { freq: freq * 1.5, type: 'sine' });
  const g2 = ctx.createGain();
  g2.gain.setValueAtTime(0.0001, t);
  g2.gain.exponentialRampToValueAtTime(peak * 0.3, t + 0.03);
  g2.gain.exponentialRampToValueAtTime(0.0001, t + dur * 0.8);
  audio.connectThrough(osc2, g2, master);
  osc2.start(t);
  osc2.stop(t + dur + 0.05);
}

// ---------------------------------------------------------------------------
// Concrete sounds
// ---------------------------------------------------------------------------

function synthSlide(): void {
  feltNoise({ durationMs: 150, centerHz: 760, sweepTo: 520, peak: 0.12, q: 0.7 });
}

function synthFlip(): void {
  // Crisp paper tick + a faint knock under it.
  knock({ pitch: 660, durationMs: 46, peak: 0.16, noise: 0.9 });
  feltNoise({ durationMs: 70, centerHz: 2200, peak: 0.08, q: 1.1 });
}

function synthPlace(): void {
  // Rounded felt "thup" — the card settles.
  knock({ pitch: 300, durationMs: 80, peak: 0.2, noise: 0.4 });
  feltNoise({ durationMs: 90, centerHz: 460, peak: 0.07, q: 0.6 });
}

function synthDeal(): void {
  // Lighter, higher place — many in a row become a riffle.
  knock({ pitch: 420, durationMs: 56, peak: 0.13, noise: 0.5 });
}

function synthDraw(): void {
  // Two quick paper ticks — turning a card off the stock.
  knock({ pitch: 540, durationMs: 50, peak: 0.15, noise: 0.85 });
  window.setTimeout(() => knock({ pitch: 600, durationMs: 44, peak: 0.12, noise: 0.85 }), 48);
}

function synthLift(): void {
  // The lightest breath — a card just peeled up off the pile.
  feltNoise({ durationMs: 80, centerHz: 1400, peak: 0.06, q: 0.9 });
}

function synthNudge(): void {
  // A soft, rounded "no" — low and short, never a buzzer.
  const ctx = audio.getAudioContext();
  const master = audio.getMasterGain();
  if (!ctx || !master) return;
  const t = ctx.currentTime;
  const osc = audio.makeOscillator(ctx, { freq: 196, type: 'sine' });
  osc.frequency.linearRampToValueAtTime(165, t + 0.16);
  const { node: g } = audio.envelope(ctx, {
    attack: 0.006,
    decay: 0.03,
    sustain: 0.5,
    release: 0.16,
    peak: 0.14,
  });
  audio.connectThrough(osc, g, master);
  osc.start(t);
  osc.stop(t + 0.22);
}

function synthAuto(): void {
  // A card flying home: the felt place + a small rising two-note chime so it
  // reads as forward PROGRESS, not just a placement.
  synthPlace();
  tone({ freq: 587.33, durationMs: 240, peak: 0.1, delayMs: 10, bright: true }); // D5
  tone({ freq: 880.0, durationMs: 320, peak: 0.09, delayMs: 90, bright: true }); // A5
}

function synthWin(): void {
  // The cascade flourish. A long warm felt sweep under a slow ascending
  // major-add9 chord — bright, generous, ~1.4s. The biggest beat in the game.
  feltNoise({ durationMs: 1300, centerHz: 900, sweepTo: 1700, peak: 0.1, q: 0.5 });
  // C–E–G–D(oct)–E(oct) climbing — an open, hopeful spread.
  const notes = [
    { f: 523.25, d: 0 },    // C5
    { f: 659.26, d: 150 },  // E5
    { f: 783.99, d: 300 },  // G5
    { f: 1046.5, d: 480 },  // C6
    { f: 1174.66, d: 660 }, // D6 (the add9 lift)
  ];
  notes.forEach((n, i) => {
    tone({ freq: n.f, durationMs: 900 - i * 80, peak: 0.12, delayMs: n.d, bright: true });
  });
  // A final soft octave shimmer to close.
  tone({ freq: 1318.51, durationMs: 1100, peak: 0.07, delayMs: 860, bright: true }); // E6
}

function synthNav(): void {
  // A single soft felt-brushed swell for hub/page transitions.
  feltNoise({ durationMs: 320, centerHz: 900, sweepTo: 1500, peak: 0.12, q: 0.6 });
}

// ---------------------------------------------------------------------------
// Public controller
// ---------------------------------------------------------------------------

class SolitaireSound {
  unlock(): void {
    audio.unlockAudio();
  }

  play(name: SolitaireSoundName): void {
    audio.playIfNotMuted(name, () => {
      audio.unlockAudio();
      switch (name) {
        case 'slide':
          synthSlide();
          return;
        case 'flip':
          synthFlip();
          return;
        case 'place':
          synthPlace();
          return;
        case 'deal':
          synthDeal();
          return;
        case 'draw':
          synthDraw();
          return;
        case 'lift':
          synthLift();
          return;
        case 'nudge':
          synthNudge();
          return;
        case 'auto':
          synthAuto();
          return;
        case 'win':
          synthWin();
          return;
        case 'nav':
          synthNav();
          return;
      }
    });
  }
}

export const sound = new SolitaireSound();
